import { formatAccruedAmount } from "@common/helpers/domains/accounting/accruedBillingRule";
import { SCHEDULE_BILLING_STATUS_LABELS } from "@common/helpers/domains/accounting/scheduleApplicationFilters";
import { subtractMonetaryValues } from "@common/helpers/monetary";
import { SCHEDULE_INVOICE_SOURCE_FALLBACK_LABEL } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import {
    ScheduleBillingStatus,
    ScheduleSourceType,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";
import { formatDateOnly } from "@helpers/date";
import { ScheduleBillingRowInfo } from "./scheduleBillingRowHelpers";

export interface ScheduleBillingStatusDisplay {
    label: string;
    tooltip: string;
}

interface ScheduleBillingStatusRow {
    sourceType: ScheduleSourceType;
    amount: number;
    billing: Pick<
        ScheduleBillingRowInfo,
        | "scheduleType"
        | "billingStatus"
        | "billedAmount"
        | "lastBilledDate"
        | "billingCount"
        | "sourceDate"
        | "sourceLabel"
    >;
}

interface ScheduleBillingCopy {
    recognized: string;
    billingEvent: string;
}

const REVENUE_BILLING_COPY: ScheduleBillingCopy = {
    recognized: "Revenue",
    billingEvent: "you invoice or get paid",
};

const EXPENSE_BILLING_COPY: ScheduleBillingCopy = {
    recognized: "Expense",
    billingEvent: "the vendor bills you or you pay",
};

function getBillingCopy(scheduleType: ScheduleType): ScheduleBillingCopy {
    return scheduleType === ScheduleType.revenue
        ? REVENUE_BILLING_COPY
        : EXPENSE_BILLING_COPY;
}

function formatBillingDate(dateOnly: string | null): string {
    return formatDateOnly(dateOnly, "MMM d, yyyy");
}

function buildInvoiceBilledTooltip(sourceLabel: string): string {
    return sourceLabel === SCHEDULE_INVOICE_SOURCE_FALLBACK_LABEL
        ? "Billed by invoice."
        : `Billed by invoice ${sourceLabel}.`;
}

function buildBilledTooltip(row: ScheduleBillingStatusRow): string {
    switch (row.sourceType) {
        case ScheduleSourceType.invoice:
            return buildInvoiceBilledTooltip(row.billing.sourceLabel);
        case ScheduleSourceType.bill:
            return `Billed by bill ${row.billing.sourceLabel}.`;
        case ScheduleSourceType.transaction:
            return `Billed by payment on ${formatBillingDate(row.billing.sourceDate)}.`;
        default: {
            const lastBilledDate = formatBillingDate(
                row.billing.lastBilledDate,
            );
            return row.billing.billingCount > 1
                ? `Billed in ${row.billing.billingCount} parts, last on ${lastBilledDate}.`
                : `Marked as billed on ${lastBilledDate}.`;
        }
    }
}

function buildTooltip(
    row: ScheduleBillingStatusRow,
    billingStatus: ScheduleBillingStatus,
): string {
    const copy = getBillingCopy(row.billing.scheduleType);
    switch (billingStatus) {
        case ScheduleBillingStatus.unbilled:
            return `${copy.recognized} is recognized but not billed yet. When ${copy.billingEvent}, choose Mark as billed from the row menu.`;
        case ScheduleBillingStatus.partiallyBilled: {
            const stillUnbilled = subtractMonetaryValues(
                row.amount,
                row.billing.billedAmount,
            );
            return `${formatAccruedAmount(stillUnbilled)} of ${formatAccruedAmount(row.amount)} is still unbilled. When ${copy.billingEvent} for the rest, choose Mark as billed.`;
        }
        case ScheduleBillingStatus.billed:
            return buildBilledTooltip(row);
    }
}

export function getScheduleBillingStatusDisplay(
    row: ScheduleBillingStatusRow,
): ScheduleBillingStatusDisplay | null {
    const { billingStatus } = row.billing;
    if (billingStatus === null) {
        return null;
    }
    return {
        label: SCHEDULE_BILLING_STATUS_LABELS[billingStatus],
        tooltip: buildTooltip(row, billingStatus),
    };
}
